import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { localDateFromUTCDate } from '@utils/formatters/date';

interface DateRange {
  start?: string | Date | null;
  end?: string | Date | null;
}

interface UseDateRangeStringOptions {
  selectedDueDateRange?: DateRange | null;
  useTranslationFormatter?: boolean;
}

/**
 * Formats a date range into a human-readable string.
 * Supports two formatting modes:
 * - Translation formatter: uses i18next 'monthAndDayYearV3' with UTC conversion
 * - Dayjs formatter: uses 'D MMM YYYY' format directly
 */
export const useDateRangeString = ({
  selectedDueDateRange,
  useTranslationFormatter = false,
}: UseDateRangeStringOptions) => {
  const { t: f } = useTranslation('format');

  // Helper to format as 'MMM D' or 'MMM D, YYYY'
  const formatMonthDay = (date: string | Date) => dayjs(date).format('MMM D');
  const formatMonthDayYear = (date: string | Date) =>
    dayjs(date).format('MMM D, YYYY');
  const formatDate = (date: string | Date) =>
    useTranslationFormatter
      ? f('monthAndDayYearV3', {
          val: localDateFromUTCDate(new Date(date)),
        })
      : dayjs(date).format('D MMM YYYY');

  return useMemo(() => {
    if (selectedDueDateRange?.start && selectedDueDateRange.end) {
      if (useTranslationFormatter) {
        return `${formatDate(selectedDueDateRange.start)} - ${formatDate(
          selectedDueDateRange.end
        )}`;
      }
      const start = dayjs(selectedDueDateRange.start);
      const end = dayjs(selectedDueDateRange.end);
      if (start.year() === end.year() && start.month() === end.month()) {
        // Same month: 'Apr 1 - 30'
        return `${formatMonthDay(start)} - ${end.format('D')}`;
      } else if (start.year() === end.year()) {
        // Same year, different month: 'Apr 1 - May 30'
        return `${formatMonthDay(start)} - ${formatMonthDay(end)}`;
      } else {
        // Different years: 'Apr 1, 2026 - May 30, 2027'
        return `${formatMonthDayYear(start)} - ${formatMonthDayYear(end)}`;
      }
    }
    if (selectedDueDateRange?.start) {
      return `Start ${formatDate(selectedDueDateRange.start)}`;
    }
    if (selectedDueDateRange?.end) {
      return `Due ${formatDate(selectedDueDateRange.end)}`;
    }
    return undefined;
  }, [selectedDueDateRange, useTranslationFormatter, f]);
};
